/*
 * AppLayout — single shared layout & navigation for every page.
 * Pulls nav items from the backend (/api/navigation) so the admin can manage them.
 * Falls back to hard-coded items if the API hasn't loaded yet.
 * Includes a login dialog for unauthenticated users.
 */
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Show, SignInButton, SignUpButton, UserButton } from '@clerk/react';
import { useQuery } from '@tanstack/react-query';
import {
  Bell, BookOpen, Camera, CircleUserRound, Drum, Flame, Home, LogOut,
  Map as MapIcon, PawPrint, PenTool, ShieldCheck, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api-client';
import { AUTH_ROLE_KEY, AUTH_TOKEN_KEY } from '@/lib/auth';
import type { NavigationMenuItem } from '@shared/types';

type AppLayoutProps = {
  children: React.ReactNode;
  container?: boolean;
  className?: string;
  contentClassName?: string;
  showTopNav?: boolean;
  /** Site-wide footer; disable on app-console pages like the admin dashboard. */
  showFooter?: boolean;
};

// ── Icon map so the backend label drives the icon ────────────────────────────
const ICON_MAP: Record<string, React.ElementType> = {
  Home,
  Poorams: Home,
  'Festival Map': MapIcon,
  Map: MapIcon,
  Directory: BookOpen,
  Elephants: PawPrint,
  'Live Feed': Camera,
  Contribute: PenTool,
};

// ── Fallback nav (shown while API loads) — mirrors the seeded header nav so
// the bottom bar's four slots don't reshuffle once the API answers.
const FALLBACK_NAV = [
  { id: 'home',       label: 'Poorams',    path: '/' },
  { id: 'map',        label: 'Map',        path: '/map' },
  { id: 'elephants',  label: 'Elephants',  path: '/elephants' },
  { id: 'directory',  label: 'Directory',  path: '/directory' },
  { id: 'feed',       label: 'Live Feed',  path: '/feed' },
  { id: 'contribute', label: 'Contribute', path: '/contribute' },
];

export function AppLayout({
  children,
  container = false,
  className,
  contentClassName,
  showTopNav = true,
  showFooter = true,
}: AppLayoutProps): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Lazy init from localStorage — no useEffect needed, no flash
  const [token, setToken] = useState(() => localStorage.getItem(AUTH_TOKEN_KEY) ?? '');
  const [role, setRole] = useState<'admin' | 'user' | ''>(
    () => (localStorage.getItem(AUTH_ROLE_KEY) ?? '') as 'admin' | 'user' | '',
  );

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Scroll detection for header shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Keeps in-memory state in sync after login/logout actions
  const syncAuth = () => {
    setToken(localStorage.getItem(AUTH_TOKEN_KEY) ?? '');
    setRole((localStorage.getItem(AUTH_ROLE_KEY) ?? '') as 'admin' | 'user' | '');
  };

  const handleLogout = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_ROLE_KEY);
    syncAuth();
    navigate('/');
  };

  // Backend nav items (header location)
  const { data: navData } = useQuery({
    queryKey: ['nav-header'],
    queryFn: () => api<{ items: NavigationMenuItem[] }>('/api/navigation?location=header'),
    staleTime: 10 * 60 * 1000,
  });

  // Live poorams for the LIVE badge count
  const { data: statsData } = useQuery({
    queryKey: ['stats'],
    queryFn: () => api<{ livePoorams: number }>('/api/stats'),
    staleTime: 30 * 1000,
  });

  const liveCount = statsData?.livePoorams ?? 0;
  // Admin and dashboard routes are auth-gated entry points — never expose them
  // in the public nav, even if the backend nav config tries to include them.
  // Admins/users reach those routes via the dedicated header buttons.
  const HIDDEN_NAV_PATHS = new Set(['/admin', '/dashboard']);
  const navItems: { id: string; label: string; path: string }[] =
    (navData?.items?.length
      ? navData.items.map((i) => ({ id: i.id, label: i.label, path: i.path }))
      : FALLBACK_NAV
    ).filter((i) => !HIDDEN_NAV_PATHS.has(i.path));

  // "/" (Poorams) also owns the /poorams/* detail pages so the tab stays lit.
  const isActive = (path: string) =>
    path === '/'
      ? location.pathname === '/' || location.pathname.startsWith('/poorams')
      : location.pathname.startsWith(path);

  // ── Nav link ──────────────────────────────────────────────────────────────
  const NavLink = ({ item, mobile = false }: { item: typeof navItems[0]; mobile?: boolean }) => {
    const Icon = ICON_MAP[item.label] ?? Home;
    const active = isActive(item.path);
    return (
      <Link
        to={item.path}
        className={cn(
          mobile
            ? 'flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-all'
            : 'relative inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-all',
          active
            ? mobile
              ? 'bg-primary/10 text-primary border border-primary/20'
              : 'text-primary bg-primary/10'
            : mobile
              ? 'text-foreground hover:bg-muted'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted',
        )}
        onClick={() => mobile && setMobileOpen(false)}
      >
        <Icon className={cn('shrink-0', mobile ? 'h-4 w-4' : 'h-3.5 w-3.5')} />
        {item.label}
        {active && !mobile && (
          <span className="absolute inset-x-3 -bottom-[10px] h-0.5 rounded-full bg-primary" />
        )}
      </Link>
    );
  };

  // ── Mobile bottom-nav link ────────────────────────────────────────────────
  const BottomNavLink = ({ item }: { item: typeof navItems[0] }) => {
    const Icon = ICON_MAP[item.label] ?? Home;
    const active = isActive(item.path);
    return (
      <Link
        to={item.path}
        className={cn(
          'group relative flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-xs font-semibold transition-colors',
          active ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
        )}
        aria-current={active ? 'page' : undefined}
      >
        <span
          className={cn(
            'flex h-9 w-12 items-center justify-center rounded-full transition-all',
            active ? 'bg-primary/12' : 'group-hover:bg-muted',
          )}
        >
          <Icon className={cn('h-4 w-4 transition-transform', active && 'scale-110')} />
        </span>
        <span className={cn('leading-none tracking-wide', active ? 'font-bold' : '')}>{item.label}</span>
      </Link>
    );
  };

  return (
    <div className={cn('flex min-h-screen flex-col', className)}>
      {showTopNav && (
        <header
          className={cn(
            'sticky top-0 z-40 border-b bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80 transition-shadow duration-200',
            scrolled ? 'shadow-md' : 'shadow-sm',
          )}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center gap-3">

            {/* ── Brand ─────────────────────────────────────────── */}
            <Link to="/" className="flex items-center gap-2.5 shrink-0 group mr-1">
              <div className="relative h-10 w-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground shadow-md group-hover:shadow-primary/40 transition-shadow shrink-0">
                <Drum className="h-5 w-5" />
                {liveCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-accent ring-2 ring-background animate-pulse" />
                )}
              </div>
              <div className="leading-none">
                <span className="text-base font-bold text-foreground block">
                  Pooram<span className="text-primary">App</span>
                </span>
                <span className="hidden sm:block text-xs text-muted-foreground uppercase tracking-[0.18em] font-semibold mt-0.5">
                  Discover Every Pooram Near You
                </span>
              </div>
            </Link>

            {/* Live count chip — visible on all sizes when active */}
            {liveCount > 0 && (
              <Link
                to="/map"
                className="hidden xs:inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-2.5 py-1 text-xs font-black uppercase tracking-widest text-accent hover:bg-accent/15 transition-colors shrink-0"
                title={`${liveCount} festival${liveCount > 1 ? 's' : ''} live now`}
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent/70" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
                </span>
                {liveCount} Live
              </Link>
            )}

            {/* ── Desktop nav — lg+: below that the brand + controls leave no
                room and labels wrap letter-by-letter (tablet header bug) ── */}
            <nav aria-label="Main" className="hidden lg:flex items-center gap-0.5 flex-1">
              {navItems.map((item) => (
                <NavLink key={item.id} item={item} />
              ))}
            </nav>

            {/* ── Right controls ─────────────────────────────────── */}
            <div className="flex items-center gap-1.5 shrink-0 ml-auto">

              {/* Live-festival alerts — links to the map where live events appear */}
              <Link
                to="/map"
                aria-label={liveCount > 0 ? `${liveCount} festival${liveCount > 1 ? 's' : ''} live now — open map` : 'Festival alerts — open map'}
                title={liveCount > 0 ? `${liveCount} live now` : 'Festival alerts'}
                className="relative h-9 w-9 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              >
                <Bell className="h-4 w-4" />
                {liveCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-accent ring-2 ring-background" />
                )}
              </Link>

              {/* Theme toggle */}
              <ThemeToggle />

              {/* Clerk user auth plus legacy admin session controls */}
              <Show when="signed-out">
                <div className="hidden sm:flex items-center gap-1.5">
                  <SignInButton mode="modal">
                    <Button variant="ghost" size="sm" className="h-8 rounded-lg px-2.5 text-xs font-bold">
                      Sign in
                    </Button>
                  </SignInButton>
                  <SignUpButton mode="modal">
                    <Button size="sm" className="h-8 rounded-lg px-2.5 text-xs font-bold">
                      Sign up
                    </Button>
                  </SignUpButton>
                </div>
                <div className="sm:hidden">
                  <SignInButton mode="modal">
                    <button
                      type="button"
                      title="Sign in"
                      className="h-8 w-8 rounded-full border-2 border-primary/30 bg-primary/5 flex items-center justify-center text-primary hover:bg-primary/15 hover:border-primary/50 transition-all"
                    >
                      <CircleUserRound className="h-3.5 w-3.5" />
                    </button>
                  </SignInButton>
                </div>
              </Show>
              <Show when="signed-in">
                <Link
                  to="/dashboard"
                  className="hidden sm:inline-flex h-8 items-center rounded-lg border border-primary/20 bg-primary/10 px-2.5 text-xs font-bold text-primary hover:bg-primary/20 transition-colors"
                >
                  Dashboard
                </Link>
                <UserButton />
              </Show>

              {token && role === 'admin' ? (
                <div className="flex items-center gap-1">
                  <Link
                    to="/admin"
                    className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground shadow-md hover:shadow-primary/40 hover:scale-105 transition-all"
                    title="Admin Dashboard"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    title="Logout"
                    className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : null}

              {/* Mobile hamburger */}
              <Button
                variant="outline"
                size="icon"
                className="lg:hidden h-8 w-8 rounded-lg"
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                onClick={() => setMobileOpen((v) => !v)}
              >
                {mobileOpen
                  ? <X className="h-4 w-4" />
                  : (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <line x1="3" y1="18" x2="21" y2="18" />
                    </svg>
                  )}
              </Button>
            </div>
          </div>

          {/* ── Mobile drawer ────── */}
          {mobileOpen && (
            <div className="lg:hidden border-t bg-background/98 backdrop-blur-md">
              <nav aria-label="Mobile" className="max-w-7xl mx-auto px-4 py-3 space-y-1">
                {navItems.map((item) => (
                  <NavLink key={item.id} item={item} mobile />
                ))}

                {/* Mobile auth section */}
                <Show when="signed-out">
                  <SignInButton mode="modal">
                    <button
                      type="button"
                      onClick={() => setMobileOpen(false)}
                      className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-all"
                    >
                      <CircleUserRound className="h-4 w-4 shrink-0 text-primary" />
                      Sign in
                    </button>
                  </SignInButton>
                  <SignUpButton mode="modal">
                    <button
                      type="button"
                      onClick={() => setMobileOpen(false)}
                      className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-all"
                    >
                      <CircleUserRound className="h-4 w-4 shrink-0 text-primary" />
                      Sign up
                    </button>
                  </SignUpButton>
                </Show>
                <Show when="signed-in">
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-all"
                  >
                    <CircleUserRound className="h-4 w-4 shrink-0 text-primary" />
                    My Dashboard
                  </Link>
                </Show>

                {token && role === 'admin' ? (
                  <>
                    <Link
                      to="/admin"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-all"
                    >
                      <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
                      Admin Dashboard
                    </Link>
                    <button
                      type="button"
                      onClick={() => { setMobileOpen(false); handleLogout(); }}
                      className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-destructive hover:bg-destructive/5 transition-all"
                    >
                      <LogOut className="h-4 w-4 shrink-0" />
                      Logout
                    </button>
                  </>
                ) : null}
              </nav>

              {/* Mobile theme row */}
              <div className="max-w-7xl mx-auto px-4 pb-4 flex items-center justify-between">
                <span />
                <ThemeToggle />
              </div>
            </div>
          )}
        </header>
      )}

      {container ? (
        <div className={'w-full flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10 lg:py-12 pb-24 lg:pb-12' + (contentClassName ? ` ${contentClassName}` : '')}>
          {children}
        </div>
      ) : (
        /* flex-1 pins the footer to the viewport bottom on short pages.
           When the footer renders, it carries the mobile bottom-nav clearance
           itself (dark bg extends behind the fixed bar instead of a light gap). */
        <div className={showFooter ? 'flex-1' : 'flex-1 pb-16 lg:pb-0'}>{children}</div>
      )}

      {showFooter && <SiteFooter />}

      {/* ── Mobile bottom navigation ─────────────────────────────────────── */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur-lg supports-[backdrop-filter]:bg-background/85 safe-bottom"
        aria-label="Primary"
      >
        <div className="flex max-w-md mx-auto px-1">
          {navItems.slice(0, 5).map((item) => (
            <BottomNavLink key={item.id} item={item} />
          ))}
        </div>
      </nav>
    </div>
  );
}
